import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';

const PrintResultPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from navigation state OR localStorage (for external browser)
  let dataFromState = location.state || {};
  
  // Check URL params for token (from external browser)
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');
  
  // If token in URL, get data from localStorage and set token
  if (tokenFromUrl && !dataFromState.results) {
    localStorage.setItem('token', tokenFromUrl);
    const printDataStr = localStorage.getItem('printData');
    if (printDataStr) {
      dataFromState = JSON.parse(printDataStr);
    }
  }
  
  const { results, questions, config } = dataFromState;

  useEffect(() => {
    if (!results) {
      navigate('/dashboard');
      return;
    }
    
    // Auto-trigger print dialog after content loads
    const timer = setTimeout(() => {
      window.print();
    }, 500);

    // Listen for after print event to go back
    const handleAfterPrint = () => {
      // Clean up localStorage if it was used
      localStorage.removeItem('printData');
      navigate(-1); // Go back to previous page (ResultPage)
    };

    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [results, navigate]);

  if (!results) {
    return null;
  }

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  const grade = getGrade(results.percentage);
  const wrongAnswers = results.total - results.score;

  const listToShow = (questions && questions.length > 0)
    ? questions
    : (results.results || []).map((r) => ({ id: r.question_id, question: r.question }));

  return (
    <div className="print-container">
      {/* Back Button - Hidden when printing */}
      <button 
        onClick={() => navigate(-1)}
        className="back-button no-print"
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          padding: '10px 20px',
          backgroundColor: '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        ← Back to Results
      </button>

      {/* Header */}
      <div className="print-header">
        <h1>Quiz Result</h1>
        <div className="print-date">{new Date().toLocaleDateString('en-IN')}</div>
      </div>

      {/* Summary Section */}
      <div className="print-summary">
        <div className="summary-row">
          <span><strong>Score:</strong> {results.score}/{results.total}</span>
          <span><strong>Percentage:</strong> {results.percentage}%</span>
          <span><strong>Grade:</strong> {grade}</span>
          <span><strong>Correct:</strong> {results.score}</span>
          <span><strong>Wrong:</strong> {wrongAnswers}</span>
          {results.rank != null && results.total_students != null && (
            <span><strong>Rank:</strong> {results.rank}/{results.total_students}</span>
          )}
        </div>
      </div>

      {/* Questions Section */}
      <div className="print-questions">
        {listToShow.map((question, index) => {
          const result = question.question_id != null && !question.opt_a
            ? question
            : (results.results || []).find((r) => r.question_id == question.id) ?? null;
          const q = result && !question.opt_a
            ? { question: result.question, correct: result.correct }
            : question;
          const isNotAnswered = result == null;

          const options = config?.useAI
            ? (q.options || [])
            : [
                { label: 'A', text: q.opt_a },
                { label: 'B', text: q.opt_b },
                { label: 'C', text: q.opt_c },
                { label: 'D', text: q.opt_d },
              ].filter((opt) => opt.text);

          return (
            <div key={question.id != null ? question.id : index} className="print-question">
              <div className="question-header">
                <span className="question-number">Q{index + 1}.</span>
                <span
                  className="question-text"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(result?.question || q.question || ''),
                  }}
                />
              </div>

              <div className="options-grid">
                {options.map((option) => {
                  const optionLabel = config?.useAI
                    ? String.fromCharCode(65 + options.indexOf(option))
                    : option.label;
                  const optionText = config?.useAI ? option.text : option.text;
                  const correctLabel = result?.correct ?? q.correct;
                  const isCorrect = config?.useAI
                    ? option.isCorrect
                    : optionLabel === correctLabel?.toUpperCase();
                  const isSelected = !isNotAnswered && result?.selected?.toUpperCase() === optionLabel;

                  return (
                    <div key={optionLabel} className="option-item">
                      <span className="option-label">
                        {optionLabel})
                      </span>
                      <span
                        className="option-text"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(optionText) }}
                      />
                      {isCorrect && <span className="correct-mark"> ✓</span>}
                      {isSelected && !isCorrect && <span className="wrong-mark"> ✗</span>}
                    </div>
                  );
                })}
              </div>

              <div className="answer-info">
                {isNotAnswered ? (
                  <span>Not answered. Correct: <strong>{(result?.correct ?? q.correct)?.toUpperCase()}</strong></span>
                ) : (
                  <span>
                    Your answer: <strong>{result.selected?.toUpperCase()}</strong>
                    {!result.is_correct && (
                      <> | Correct: <strong>{result.correct?.toUpperCase()}</strong></>
                    )}
                  </span>
                )}
              </div>

              {/* Explanation */}
              {(result?.explanation || q?.explanation) && (
                <div className="explanation-box">
                  <strong>💡 Explanation:</strong>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(result?.explanation ?? q?.explanation ?? ''),
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Print Styles */}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Noto Sans', 'Noto Sans Devanagari', Arial, sans-serif;
          font-size: 10pt;
          line-height: 1.3;
          color: #000;
          background: #fff;
        }

        .print-container {
          max-width: 100%;
          padding: 15mm;
          background: white;
        }

        .print-header {
          text-align: center;
          border-bottom: 2px solid #000;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }

        .print-header h1 {
          font-size: 18pt;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .print-date {
          font-size: 9pt;
          color: #666;
        }

        .print-summary {
          background: #f5f5f5;
          border: 1px solid #ddd;
          padding: 8px 12px;
          margin-bottom: 12px;
          border-radius: 4px;
        }

        .summary-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 9pt;
        }

        .summary-row span {
          white-space: nowrap;
        }

        .print-questions {
          margin-top: 12px;
        }

        .print-question {
          margin-bottom: 12px;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          page-break-inside: avoid;
          background: #fff;
        }

        .question-header {
          display: flex;
          margin-bottom: 6px;
          gap: 6px;
        }

        .question-number {
          font-weight: bold;
          flex-shrink: 0;
          min-width: 30px;
        }

        .question-text {
          flex: 1;
          font-weight: 600;
        }

        .options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px 12px;
          margin: 6px 0 6px 36px;
          font-size: 9pt;
        }

        .option-item {
          display: flex;
          align-items: baseline;
          gap: 4px;
        }

        .option-label {
          font-weight: bold;
          flex-shrink: 0;
        }

        .option-text {
          flex: 1;
        }

        .correct-mark {
          color: #059669;
          font-weight: bold;
        }

        .wrong-mark {
          color: #dc2626;
          font-weight: bold;
        }

        .answer-info {
          margin-top: 6px;
          margin-left: 36px;
          font-size: 9pt;
          color: #444;
          padding: 4px 8px;
          background: #f9f9f9;
          border-left: 3px solid #666;
        }

        .explanation-box {
          margin-top: 6px;
          margin-left: 36px;
          font-size: 9pt;
          color: #444;
          padding: 6px 8px;
          background: #e3f2fd;
          border-left: 3px solid #2196f3;
          line-height: 1.4;
        }

        .explanation-box strong {
          display: block;
          margin-bottom: 4px;
          color: #1976d2;
        }

        @media print {
          body {
            background: white !important;
          }

          .print-container {
            padding: 10mm;
          }

          .print-question {
            page-break-inside: avoid;
          }

          .no-print,
          .back-button {
            display: none !important;
          }

          @page {
            margin: 15mm;
            size: A4;
          }
        }

        @media screen {
          body {
            background: #f3f4f6;
          }

          .print-container {
            max-width: 210mm;
            margin: 20px auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </div>
  );
};

export default PrintResultPage;
